import { PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import {
  BACKER_SHARE_MINT_SEED,
  CLAIM_RECEIPT_SEED,
  CLAIM_RIGHTS_MINT_SEED,
  EVENT_AUTHORITY_SEED,
  GIFT_ADMIN_SEED,
  GIFT_CONFIG_SEED,
  GIFT_SEED,
  MARKET_SEED,
  METADATA_SEED,
  OUTCOME_MINT_SEED,
  PLATFORM_FEE_VAULT_SEED,
  POSITION_SEED,
  SELL_CAP_CONFIG_SEED,
  SELL_CAP_SEED,
} from '../constants/seeds.js';

/**
 * Encode a u8 value as a 1-byte buffer for PDA seed derivation.
 *
 * The Rust contract derives outcome-keyed PDAs with `&[outcome_index]` —
 * a single byte. The TS mirror MUST match (`u8`, not `u16`); the previous
 * `u16LeBuffer(outcomeIndex)` produced 2-byte seeds and yielded PDAs that
 * don't exist on chain (broken trade-form on-chain reads, broken outcome
 * fetches).
 *
 * @param value - Unsigned 8-bit integer (0..=255)
 * @returns 1-byte Buffer
 */
function u8Buffer(value: number): Buffer {
  if (value < 0 || value > 255 || !Number.isInteger(value)) {
    throw new RangeError(`u8 out of range: ${value}`);
  }
  const buf = Buffer.alloc(1);
  buf.writeUInt8(value, 0);
  return buf;
}

/**
 * Encode a u64 (BN) value as 8-byte little-endian buffer for PDA seed derivation.
 * Matches Rust `market_nonce.to_le_bytes()`.
 */
function u64LeBuffer(value: BN): Buffer {
  return value.toArrayLike(Buffer, 'le', 8);
}

/**
 * Derive Market PDA. Seeds: `[b"market", creator[32], market_nonce_le[8]]`.
 *
 * @param creator - Wallet pubkey of the user creating the market (on-chain `creator`)
 * @param marketNonce - u64 nonce chosen by the creator (typically a fresh random 8 bytes)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveMarketPda(
  creator: PublicKey,
  marketNonce: BN,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [MARKET_SEED, creator.toBuffer(), u64LeBuffer(marketNonce)],
    programId,
  );
}

/**
 * Derive Outcome Mint PDA. Seeds: [b"outcome_mint", market[32], outcome_index[1]].
 *
 * @param market - Market account public key
 * @param outcomeIndex - Outcome index (u8)
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveOutcomeMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [OUTCOME_MINT_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * Derive Claim Receipt PDA. Seeds: [b"claim_receipt", market[32], user[32]].
 *
 * @param market - Market account public key
 * @param user - User wallet public key
 * @param programId - Morfi opinion program ID
 * @returns Tuple of [PDA PublicKey, bump seed]
 */
export function deriveClaimReceiptPda(
  market: PublicKey,
  user: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CLAIM_RECEIPT_SEED, market.toBuffer(), user.toBuffer()],
    programId,
  );
}

/** Current GiftConfig binding: [b"gift_config", market_config[32], label[32]]. */
export function deriveGiftConfigPda(
  marketConfig: PublicKey,
  label: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  if (label.length !== 32) {
    throw new Error(`deriveGiftConfigPda: label must be exactly 32 bytes, got ${label.length}`);
  }
  return PublicKey.findProgramAddressSync(
    [GIFT_CONFIG_SEED, marketConfig.toBuffer(), label],
    programId,
  );
}

/**
 * Derive GiftAdmin PDA — the on-chain authority for the source treasury
 * ATA holding gift tokens. Seeds: [b"gift_admin", gift_config[32]].
 */
export function deriveGiftAdminPda(
  giftConfig: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([GIFT_ADMIN_SEED, giftConfig.toBuffer()], programId);
}

/**
 * Derive Gift PDA — one per (gift_config, mint, handler_hash).
 * Seeds: [b"gift", gift_config[32], mint[32], handler_hash[32]].
 */
export function deriveGiftPda(
  giftConfig: PublicKey,
  mint: PublicKey,
  handlerHash: Uint8Array,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  if (handlerHash.length !== 32) {
    throw new Error(
      `deriveGiftPda: handlerHash must be exactly 32 bytes, got ${handlerHash.length}`,
    );
  }
  return PublicKey.findProgramAddressSync(
    [GIFT_SEED, giftConfig.toBuffer(), mint.toBuffer(), handlerHash],
    programId,
  );
}

/**
 * Claim Rights Mint PDA. Seeds: `[b"claim_rights_mint", market[32], outcome_index[1]]`.
 */
export function deriveClaimRightsMintPda(
  market: PublicKey,
  outcomeIndex: number,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [CLAIM_RIGHTS_MINT_SEED, market.toBuffer(), u8Buffer(outcomeIndex)],
    programId,
  );
}

/**
 * Backer Share Mint PDA. Seeds: `[b"backer_share_mint", market[32]]`.
 */
export function deriveBackerShareMintPda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([BACKER_SHARE_MINT_SEED, market.toBuffer()], programId);
}

/** Fee-vault shards per market config; mirrors the program's `PLATFORM_FEE_VAULT_COUNT`. */
export const PLATFORM_FEE_VAULT_COUNT = 8;

/**
 * Platform Fee Vault PDA. Seeds: `[b"platform_fee_vault", market_config[32], shard_u8]`.
 * Builders write to shard 0; the program accepts any shard below `PLATFORM_FEE_VAULT_COUNT`.
 */
export function derivePlatformFeeVaultPda(
  marketConfig: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
  shard: number = 0,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [PLATFORM_FEE_VAULT_SEED, marketConfig.toBuffer(), u8Buffer(shard)],
    programId,
  );
}

/**
 * Anchor `#[event_cpi]` event-authority PDA. Seeds: `[b"__event_authority"]`.
 * Owned by the program itself; Anchor adds this account automatically when
 * the IDL is loaded via `Program`, but exposed here for callers that bypass
 * the typed methods API.
 */
export function deriveEventAuthorityPda(
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([EVENT_AUTHORITY_SEED], programId);
}

/** Sell-cap program: per-market cap switch. Seeds: `[b"sell_cap", market[32]]`. */
export function deriveSellCapStatePda(
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync([SELL_CAP_SEED, market.toBuffer()], programId);
}

/** Sell-cap program: preset opt-in. Seeds: `[b"sell_cap_config", market_config[32]]`. */
export function deriveSellCapConfigPda(
  marketConfig: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [SELL_CAP_CONFIG_SEED, marketConfig.toBuffer()],
    programId,
  );
}

/**
 * Sell-cap program: a trader's cost-basis position for one outcome.
 * Seeds: `[b"position", market[32], outcome_index[1], owner[32]]`.
 */
export function derivePositionPda(
  market: PublicKey,
  outcomeIndex: number,
  owner: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [POSITION_SEED, market.toBuffer(), u8Buffer(outcomeIndex), owner.toBuffer()],
    programId,
  );
}

/** MPL Token Metadata program id (mainnet + devnet — same on both clusters). */
const MPL_TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

/**
 * Token Metadata PDA — the Metaplex-owned account holding name/symbol/uri for
 * a given SPL mint. Seeds (in MPL_TOKEN_METADATA's address space):
 *   `[b"metadata", mpl_token_metadata_program_id[32], mint[32]]`
 */
export function deriveTokenMetadataPda(mint: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [METADATA_SEED, MPL_TOKEN_METADATA_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    MPL_TOKEN_METADATA_PROGRAM_ID,
  );
}
